import {
	expressQuerySelector,
	expressEventListener,
	expressQuerySelectorAll,
	htmlToElement,
	expressAddClass,
	expressRemoveClass,
} from "../../common/html";
import { managementLogin, removeLoginInfo, setLoginInfo } from "../../services/inline-edit-service";
import { compositeDisposable } from "../../utils/disposable";

interface IInlineEditCardComponent {
	dispose: () => void;
}

export function InitInlineEditCard(): IInlineEditCardComponent {
	const element = expressQuerySelector(document, ".technical-inline-edit", false);
	if (!element) return;

	const hideClass = "u-hide";

	const button = expressQuerySelector(element, ".technical-editing-card-collapse-button");
	const dataContainer = expressQuerySelector(element, ".technical-inline-edit__data-container");
	const loginContainer = expressQuerySelector<HTMLElement>(element, ".technical-inline-edit__login-container");
	const loggingInContainer = expressQuerySelector<HTMLElement>(element, ".technical-inline-edit__logging-in-container");
	const logoutButtonEl = expressQuerySelector<HTMLElement>(element, ".technical-inline-edit-logout");

	const initGoogleClient = () => {
		// authenticate - login
		google.accounts.id.initialize({
			client_id: loginContainer.dataset.googleClientId,
			use_fedcm_for_button: true, // use_fedcm_for_button is missing in the definition-file but is required for fedcm to work
			callback: async (response: google.accounts.id.CredentialResponse) => {
				if (!response) return;

				// hide the button
				expressAddClass(loginContainer.firstElementChild as HTMLElement, "u-hide");
				// show the loading-div
				expressRemoveClass(loggingInContainer, "u-hide");

				// set credentials
				const userCredentions = await managementLogin(response.credential);
				if (!userCredentions || !userCredentions.email) {
					expressRemoveClass(loginContainer.firstElementChild as HTMLElement, "u-hide");
					expressAddClass(loggingInContainer, "u-hide");
					return;
				}

				await setLoginInfo(userCredentions);
				window.location.reload();
			},
		});

		google.accounts.id.renderButton(loginContainer, {
			type: "standard",
			shape: "rectangular",
			size: "large",
			width: 250,
			locale: window.context.locale,
		});
	};

	const disposible = compositeDisposable([
		expressEventListener(button, "click", () => {
			element.classList.toggle("t-inline-editing-card--collapsed");
		}),
		expressEventListener(logoutButtonEl, "click", async () => {
			// show login again
			loginContainer.classList.remove(hideClass);
			dataContainer.classList.add(hideClass);

			await removeLoginInfo();

			window.location.reload();
		}),
	]);

	const initGoogleButton = () => {
		// Add the script only once
		const googleScriptSrc = "https://accounts.google.com/gsi/client?hl=" + window.context.language;
		const scripts = document.getElementsByTagName("script");
		for (const item of scripts) {
			if (item.src === googleScriptSrc) {
				// Script-tag already exists. Manually call init-func
				initGoogleClient();
				return;
			}
		}

		// load google script async
		const scriptEl = document.createElement("script");
		scriptEl.type = "text/javascript";
		scriptEl.async = true;
		scriptEl.defer = true;
		scriptEl.onload = initGoogleClient;
		scriptEl.src = googleScriptSrc;
		const firstScriptEl = document.getElementsByTagName("script")[0];
		firstScriptEl.parentElement!.insertBefore(scriptEl, firstScriptEl);
	};

	const addHoverHtml = () => {
		const inlineEditEls = expressQuerySelectorAll<HTMLElement>(
			document,
			"[data-inlineedit-cmskey][data-inlineedit-cmskey]",
		);
		if (inlineEditEls?.length == 0) return;

		inlineEditEls.forEach((inlineEditEl) => {
			const parent = inlineEditEl.parentNode;
			const wrapper = document.createElement("div");
			expressAddClass(wrapper, "t-inline-editing-zone");

			parent.replaceChild(wrapper, inlineEditEl); // add wrapper to the parent
			wrapper.appendChild(inlineEditEl); // add element to the wrapper

			// add extra html to the wrapper
			wrapper.appendChild(
				htmlToElement(
					"<div class='t-inline-editing-zone__edit'> " +
						"<span class='v2-a-icon v2-a-icon--small'> " +
						"<i class='v2-a-icon__icon'> " +
						"<svg class='v2-a-icon__svg'> " +
						"<use class='v2-a-icon__use' href='/svg/pencil-symbol.svg#pencil-symbol'> " +
						"</use>" +
						"</svg>" +
						"</i>" +
						"</span>" +
						"</div>",
				),
			);
		});
	};

	initGoogleButton();
	addHoverHtml();

	return {
		dispose: disposible.dispose,
	};
}
